import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { normalizeCarrierSummary } from "./carrierTracking";
import { isShipmentTrackingMode, isShipmentTripUnitState, normalizeMerchandise } from "./types";
import type {
  Shipment,
  ShipmentEvent,
  ShipmentPlace,
  ShipmentTrackingMode,
} from "./types";

export interface ShipmentListParams {
  page?: number;
  limit?: number;
  trip_id?: string;
  without_trip?: boolean;
}

interface ListMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

interface ShipmentListBody {
  data?: unknown[];
  meta?: ListMeta;
}

interface ShipmentDetailBody {
  data?: { data?: unknown } | unknown;
}

export interface ShipmentListResult {
  data: Shipment[];
  meta?: ListMeta;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

const asNumber = (value: unknown): number | null => {
  const parsed = typeof value === "string" ? Number(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : null;
};

const normalizePlace = (value: unknown): ShipmentPlace => {
  const raw = asRecord(value);
  return {
    lat: asNumber(raw.lat),
    long: asNumber(raw.long ?? raw.lng),
    address: typeof raw.address === "string" ? raw.address : null,
  };
};

const normalizeDate = (value: unknown): string => {
  if (typeof value === "string") return value;
  const raw = asRecord(value);
  const seconds = asNumber(raw._seconds ?? raw.seconds);
  return seconds !== null ? new Date(seconds * 1000).toISOString() : "";
};

const normalizeEvent = (value: unknown, index: number): ShipmentEvent => {
  const raw = asRecord(value);
  const event: ShipmentEvent = {
    id: asString(raw.id, `ev-${index}`),
    event_type: asString(raw.event_type),
    lat: asNumber(raw.lat),
    long: asNumber(raw.long ?? raw.lng),
    address: typeof raw.address === "string" ? raw.address : null,
    note: typeof raw.note === "string" ? raw.note : null,
    date: normalizeDate(raw.date ?? raw.created_at),
  };

  if (Array.isArray(raw.file_urls)) {
    event.file_urls = raw.file_urls;
  }
  // Se conservan para que reescribir el historial (PUT /shipment) no borre la
  // llave de deduplicación de los eventos de paquetería.
  if (typeof raw.source === "string") event.source = raw.source;
  if (typeof raw.client_event_id === "string") event.client_event_id = raw.client_event_id;
  if (typeof raw.carrier_status === "string") event.carrier_status = raw.carrier_status;

  return event;
};

export const normalizeShipment = (value: unknown): Shipment => {
  const raw = asRecord(value);
  const history = Array.isArray(raw.event_history) ? raw.event_history : [];

  const contacts =
    Array.isArray(raw.contacts) && raw.contacts.length > 0
      ? raw.contacts.filter((c) => c && typeof c.id === "string")
      : null;

  return {
    id: asString(raw.id),
    client_id: asString(raw.client_id),
    client_name: asString(raw.client_name),
    shipment_name: asString(raw.shipment_name),
    company_id: typeof raw.company_id === "string" ? raw.company_id : null,
    actual_device_id:
      typeof raw.actual_device_id === "string" ? raw.actual_device_id : null,
    origin: normalizePlace(raw.origin),
    destination: normalizePlace(raw.destination),
    tracking_number: asString(raw.tracking_number),
    transportation_type: asString(raw.transportation_type),
    status_id: asString(raw.status_id),
    note: typeof raw.note === "string" ? raw.note : null,
    event_history: history
      .map(normalizeEvent)
      .sort((a, b) => a.date.localeCompare(b.date)),
    contacts,
    notification_frequency: typeof raw.notification_frequency === "string" ? raw.notification_frequency : null,
    merchandise: normalizeMerchandise(raw.merchandise),
    tracking_link_id:
      typeof raw.tracking_link_id === "string" ? raw.tracking_link_id : null,
    trip_id: typeof raw.trip_id === "string" ? raw.trip_id : null,
    trip_unit_state: isShipmentTripUnitState(raw.trip_unit_state) ? raw.trip_unit_state : null,
    smart_tracking: isShipmentTrackingMode(
      typeof raw.smart_tracking === "string" ? raw.smart_tracking : null,
    )
      ? (raw.smart_tracking as ShipmentTrackingMode)
      : "manual",
    carrier_tracking: normalizeCarrierSummary(raw.carrier_tracking),
    created_at: raw.created_at,
    updated_at: raw.updated_at,
  };
};

export function useShipmentsList(
  params: ShipmentListParams = {},
  options: { enabled?: boolean; refetchInterval?: number } = {},
) {
  const cid = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.shipments.list(cid, params),
    enabled: options.enabled ?? true,
    ...(options.refetchInterval ? { refetchInterval: options.refetchInterval } : {}),
    queryFn: ({ signal }) =>
      getJson<ShipmentListBody>(
        "/shipmentList",
        { page: 1, limit: 20, ...params },
        { signal },
      ).then(
        (body): ShipmentListResult => ({
          data: (body.data ?? []).map(normalizeShipment),
          meta: body.meta,
        }),
      ),
  });
}

export function useShipmentByReference(
  reference: string | null | undefined,
  options: { refetchInterval?: number } = {},
) {
  const query = useShipmentsList({ page: 1, limit: 100 }, options);
  const shipment =
    reference && query.data
      ? (query.data.data.find(
          (item) => item.tracking_number === reference || item.id === reference,
        ) ?? null)
      : null;
  return { ...query, shipment };
}

export function useShipment(id: string | null | undefined) {
  const cid = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.shipments.detail(cid, id ?? null),
    enabled: !!id,
    queryFn: ({ signal }) =>
      getJson<ShipmentDetailBody>("/shipment", { id }, { signal }).then((body) =>
        normalizeShipment(asRecord(body.data).data ?? body.data),
      ),
  });
}

export interface Status {
  id: string;
  name: string;
  description?: string;
  color?: string;
}

interface StatusListBody {
  data?: Status[];
}

export interface ShipmentContact {
  id: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  company_id: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ContactListBody {
  data?: ShipmentContact[];
}

export function useShipmentStatuses() {
  return useQuery({
    queryKey: ["shipmentStatuses"],
    queryFn: ({ signal }) =>
      getJson<StatusListBody>("/statusesList", { limit: 100 }, { signal }).then((body) => ({
        data: (body.data ?? []) as Status[],
      })),
  });
}

export function useShipmentContacts() {
  const cid = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: ["shipmentContacts", cid],
    queryFn: ({ signal }) =>
      getJson<ContactListBody>("/contactList", { limit: 500, page: 1 }, { signal }).then((body) => ({
        data: (body.data ?? []) as ShipmentContact[],
      })),
  });
}
